// Le logo ramène en HAUT de l'accueil (remarque de Marc, 19/09/2026 : « quand je clique sur
// le logo je ne remonte pas en haut de la page »).
//
// Le bug : le logo est un `<Link to="/">`, et il échoue de deux façons différentes.
//
//   · Sur l'accueil — la page la plus longue du site — la destination est DÉJÀ la position
//     courante. React Router ne re-notifie rien, aucun composant ne se remonte, et le
//     navigateur ne défile que pour les liens d'ancre : le clic ne produit donc RIEN. C'est
//     exactement le geste qui a fait écrire Marc, et c'est la même famille que le bug
//     d'ancre du 02/08 (components/AnchorLink.tsx) : React Router ne fait rien quand on lui
//     demande là où l'on est déjà.
//   · Depuis une autre page, le symptôme est plus discret mais de même nature : une
//     navigation côté client ne remet pas le défilement à zéro. Descendre dans un
//     observatoire puis cliquer le logo ouvre l'accueil au milieu de la page.
//
// Le correctif : on ne confie plus le défilement au navigateur, on le fait nous-mêmes — et
// dans un seul composant, parce que le logo est cliquable à HUIT endroits (en-tête de la
// vitrine, en-tête des pages coquille, topbar de l'observatoire, et les cinq pages de
// connexion). Une copie par en-tête, c'est sept occasions d'oublier le prochain correctif.
//
// Ce qu'on ne casse pas : Ctrl/⌘/Maj/Alt + clic et le clic molette ouvrent un nouvel onglet.
// Ces gestes-là sortent AVANT tout `preventDefault` — un lien qu'on ne peut plus ouvrir à
// côté est un lien cassé, même s'il défile bien.
import { Link, useLocation } from 'react-router-dom'
import type { ComponentProps, MouseEvent } from 'react'

import { comportementDefilement } from '../animationsReduites'
import { useBaseObs } from '../obs/baseObs'
import BadgeEnvironnement from './BadgeEnvironnement'

// `to` est fixé par le composant : ce lien-là ne mène qu'à l'accueil. Tout le reste
// (className, aria-label, children…) passe au `Link` sans être recopié ici.
type Props = Omit<ComponentProps<typeof Link>, 'to'>

function remonterEnHaut(comportement: ScrollBehavior): void {
  window.scrollTo({ top: 0, left: 0, behavior: comportement })
}

export default function LienAccueil({ onClick, children, ...rest }: Props) {
  const { pathname, hash } = useLocation()
  // Dans une maquette vivante de /admin/design, l'accueil est celui de la maquette.
  const baseObs = useBaseObs()
  const accueil = baseObs ? `${baseObs}/accueil` : '/'

  const gererClic = (e: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e)
    if (e.defaultPrevented) return
    // Nouvel onglet demandé : on ne touche ni à la navigation ni au défilement.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return

    if (pathname !== accueil) {
      // On laisse React Router naviguer — il n'y a aucune raison de lui reprendre son
      // travail — et on remet le défilement à zéro tout de suite. Instantané, JAMAIS en
      // douceur : la page qu'on quitte est déjà en train de disparaître, animer son
      // défilement ne donnerait qu'un soubresaut avant le changement d'écran.
      remonterEnHaut('auto')
      return
    }

    // Déjà sur l'accueil : React Router ne fera rien (même destination que la position
    // courante). C'est ici, et seulement ici, qu'on prend la main.
    e.preventDefault()
    remonterEnHaut(comportementDefilement())
    // Une ancre laissée dans l'URL (/#recherche, /#tarifs) décrirait une position où l'on
    // n'est plus, et un rechargement redescendrait. `replaceState` la retire sans empiler
    // d'entrée d'historique : le bouton Retour continue de ramener à la page précédente,
    // pas au clic précédent. Même précaution que AnchorLink.
    if (hash) window.history.replaceState(null, '', accueil)
  }

  return (
    <Link to={accueil} onClick={gererClic} {...rest}>
      {children}
      {/* « Staging » au staging seulement (variable de build) : rien en production. */}
      <BadgeEnvironnement />
    </Link>
  )
}
